import React, { useEffect, useRef } from 'react';
import './WorkflowAnimation.css';

const WorkflowAnimation = () => {
  const containerRef = useRef(null);
    const currentSceneRef = useRef(0);
    const totalScenesRef = useRef(4);
    const pauseDuration = 2000;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const moveToScene = (sceneIndex, immediate = false) => {
      const scenes = container.querySelectorAll('.workflow-scene-wrapper');
      if (scenes.length === 0) return;

      const sceneWidth = scenes[0].offsetWidth;
      const gap = window.innerWidth; // Gap = full viewport width

      // Calculate offset - continue moving left infinitely
      const offset = sceneIndex * (sceneWidth + gap);

      // Center the current scene within the actual container (not window)
      const viewport = container.parentElement;
      const viewportWidth = viewport ? viewport.offsetWidth : window.innerWidth;
      const centerOffset = (viewportWidth - sceneWidth) / 2;

      const transform = -(offset - centerOffset);
      
      if (immediate) {
        container.style.transition = 'none';
        container.style.transform = `translateX(${transform}px)`;
        // Force reflow
        void container.offsetHeight;
        container.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        container.style.transform = `translateX(${transform}px)`;
      }
    };

    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const startLoop = async () => {
      // Clone the first scene and append it at the end for seamless looping
      const scenes = Array.from(container.querySelectorAll('.workflow-scene-wrapper'));
      const firstSceneClone = scenes[0].cloneNode(true);
      container.appendChild(firstSceneClone);
      
      // Initial position
      moveToScene(0);
      await wait(pauseDuration);
      
      let sceneIndex = 0;
      
      // Continuous loop
      while (true) {
        sceneIndex++;
        currentSceneRef.current = sceneIndex;
        
        // Show arrow before transition (only for original scenes)
        if (sceneIndex < totalScenesRef.current) {
          const arrows = Array.from(container.querySelectorAll('.workflow-arrow'));
          const arrowIndex = sceneIndex - 1;
          if (arrowIndex < arrows.length && arrows[arrowIndex] && !arrows[arrowIndex].classList.contains('workflow-arrow-hidden')) {
            arrows[arrowIndex].classList.add('workflow-visible');
            await wait(500);
          }
        }
        
        // Move to next scene
        moveToScene(sceneIndex);
        await wait(800);
        
        // When we reach the cloned first scene, reset to actual first scene
        if (sceneIndex === totalScenesRef.current) {
          await wait(pauseDuration);
          sceneIndex = 0;
          currentSceneRef.current = 0;
          moveToScene(0, true); // Jump back instantly without animation
          await wait(50);
        } else {
          await wait(pauseDuration);
        }
      }
    };

    startLoop();

    // Handle window resize
    const handleResize = () => {
      moveToScene(currentSceneRef.current);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="workflow-animation-wrapper">
      <div className="workflow-viewport">
        <div ref={containerRef} className="workflow-flowchart-container">
          
          {/* Scene 1: Agentic Science */}
          <div className="workflow-scene-wrapper">
            <div className="workflow-card">
              <div className="workflow-neural-network">
                <svg viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <marker id="workflow-arrowhead" markerWidth="6" markerHeight="6" 
                            refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                      <path d="M0,0 L0,6 L6,3 z" fill="#ffffff" opacity="0.5" />
                    </marker>
                  </defs>
                  
                  {/* Layer 1: Input (3 nodes) */}
                  <circle cx="50" cy="90" r="10" className="workflow-node" />
                  <circle cx="50" cy="130" r="10" className="workflow-node" />
                  <circle cx="50" cy="170" r="10" className="workflow-node" />
                  
                  {/* Layer 2: Hidden 1 (4 nodes) */}
                  <circle cx="170" cy="60" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="170" cy="105" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="170" cy="155" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="170" cy="200" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  
                  {/* Layer 3: Hidden 2 (4 nodes) */}
                  <circle cx="290" cy="60" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="290" cy="105" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="290" cy="155" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  <circle cx="290" cy="200" r="12" className="workflow-node" fill="none" stroke="#ffffff" strokeWidth="2" />
                  
                  {/* Layer 4: Output (2 nodes) */}
                  <circle cx="410" cy="105" r="10" className="workflow-node" />
                  <circle cx="410" cy="155" r="10" className="workflow-node" />
                  
                  {/* Connections Layer 1 to 2 with arrowheads */}
                  <line x1="60" y1="90" x2="160" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="90" x2="160" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="90" x2="160" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="90" x2="160" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="60" y1="130" x2="160" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="130" x2="160" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="130" x2="160" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="130" x2="160" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="60" y1="170" x2="160" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="170" x2="160" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="170" x2="160" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="60" y1="170" x2="160" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  {/* Connections Layer 2 to 3 with arrowheads */}
                  <line x1="180" y1="60" x2="280" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="60" x2="280" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="60" x2="280" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="60" x2="280" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="180" y1="105" x2="280" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="105" x2="280" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="105" x2="280" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="105" x2="280" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="180" y1="155" x2="280" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="155" x2="280" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="155" x2="280" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="155" x2="280" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="180" y1="200" x2="280" y2="60" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="200" x2="280" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="200" x2="280" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="180" y1="200" x2="280" y2="200" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  {/* Connections Layer 3 to 4 with arrowheads */}
                  <line x1="300" y1="60" x2="400" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="300" y1="60" x2="400" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="300" y1="105" x2="400" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="300" y1="105" x2="400" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="300" y1="155" x2="400" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="300" y1="155" x2="400" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  
                  <line x1="300" y1="200" x2="400" y2="105" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                  <line x1="300" y1="200" x2="400" y2="155" className="workflow-connection" markerEnd="url(#workflow-arrowhead)" strokeWidth="1.5" />
                </svg>
            </div>
            <h2>Do agentic science</h2>
            </div>
            <div className="workflow-arrow">→</div>
          </div>

          {/* Scene 2: Communicate Professionally */}
          <div className="workflow-scene-wrapper">
            <div className="workflow-card">
            <div className="workflow-media-icons">
              <div className="workflow-icon-row">
                <div className="workflow-icon">
                  <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                    <rect x="5" y="10" width="40" height="30" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <line x1="5" y1="17" x2="45" y2="17" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="15" cy="27" r="3" fill="currentColor"/>
                    <line x1="22" y1="24" x2="40" y2="24" stroke="currentColor" strokeWidth="2"/>
                    <line x1="22" y1="30" x2="35" y2="30" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>Slide decks</span>
                </div>
                <div className="workflow-icon workflow-doi-text">
                  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="100" cy="100" r="95" fill="none" stroke="currentColor" strokeWidth="3"/>
                    <text x="100" y="125" textAnchor="middle" fontFamily="'Andale Mono', 'Monaco', 'Courier New', monospace" fontSize="60" fontWeight="bold" fill="currentColor">+DOI</text>
                  </svg>
                  <span>&nbsp;</span>
                </div>
              </div>
            </div>
            <h2>Communicate professionally</h2>
            <p className="workflow-subtitle">Slide decks, digital object identifiers</p>
            </div>
            <div className="workflow-arrow">→</div>
          </div>

          {/* Scene 3: Generate Public Media */}
          <div className="workflow-scene-wrapper">
            <div className="workflow-card">
            <div className="workflow-media-icons">
              <div className="workflow-icon-row">
                <div className="workflow-icon">
                  <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                    <rect x="5" y="12" width="40" height="26" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <polygon points="20,22 20,32 32,27" fill="currentColor"/>
                  </svg>
                  <span>Video</span>
                </div>
                <div className="workflow-icon workflow-phone-icon">
                  <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                    <rect x="14" y="5" width="22" height="40" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <clipPath id="workflow-phone-screen">
                      <rect x="16" y="8" width="18" height="32"/>
                    </clipPath>
                    <g clipPath="url(#workflow-phone-screen)" className="workflow-scrolling-content">
                      <line x1="18" y1="18" x2="32" y2="18" stroke="currentColor" strokeWidth="1"/>
                      <line x1="18" y1="21" x2="30" y2="21" stroke="currentColor" strokeWidth="0.5"/>
                      <line x1="18" y1="23" x2="31" y2="23" stroke="currentColor" strokeWidth="0.5"/>
                      <rect x="19" y="26" width="10" height="6" fill="currentColor" opacity="0.3"/>
                      <circle cx="24" cy="29" r="1.5" fill="currentColor"/>
                      <line x1="18" y1="34" x2="32" y2="34" stroke="currentColor" strokeWidth="1"/>
                      <line x1="18" y1="37" x2="29" y2="37" stroke="currentColor" strokeWidth="0.5"/>
                      <line x1="18" y1="39" x2="31" y2="39" stroke="currentColor" strokeWidth="0.5"/>
                      <path d="M 19,43 L 22,46 L 26,42 L 30,45" stroke="currentColor" strokeWidth="1" fill="none"/>
                      <line x1="18" y1="50" x2="32" y2="50" stroke="currentColor" strokeWidth="1"/>
                      <line x1="18" y1="53" x2="28" y2="53" stroke="currentColor" strokeWidth="0.5"/>
                    </g>
                    <circle cx="25" cy="42" r="1.5" fill="none" stroke="currentColor" strokeWidth="1"/>
                  </svg>
                  <span>Scrollytelling stories</span>
                </div>
              </div>
            </div>
            <h2>Generate public-facing media</h2>
            </div>
            <div className="workflow-arrow">→</div>
          </div>

          {/* Scene 4: Inspiration */}
          <div className="workflow-scene-wrapper">
            <div className="workflow-card">
            <div className="workflow-media-icons">
              <div className="workflow-icon-row">
                <div className="workflow-icon workflow-lightbulb-icon">
                  <img src="/lightbulb_icon.png" alt="Lightbulb Icon" />
                  <span>&nbsp;</span>
                </div>
              </div>
            </div>
            <h2>Inspiration</h2>
            </div>
            <div className="workflow-arrow workflow-arrow-hidden">→</div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default WorkflowAnimation;
